import { isoMonday } from '@/lib/date-utils'

export type WeekRankMode = 'repeat' | 'each'

export interface BrockenLesson {
  entryId: string
  date: string
  timeSlotId: string
  subjectId?: string
  subjectName?: string
  klassName?: string
  startTime?: string
  endTime?: string
}

export type BrockenCutKind = 'pause' | 'subject' | 'class'

export interface BrockenCut {
  kind: BrockenCutKind
  minutes: number
}

export interface BrockenUnit {
  date: string
  timeSlotIds: string[]
  label: string
  shortLabel: string
  weekday: number
  fingerprint: string
  lessons: BrockenLesson[]
  cutAfter: BrockenCut | null
}

const GAP_MAX_MINUTES = 20
const WD = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']

export function weekdayOf(iso: string): number {
  const parts = iso.split('-').map(Number)
  const y = parts[0] ?? 0
  const m = parts[1] ?? 1
  const d = parts[2] ?? 1
  return new Date(y, m - 1, d).getDay()
}

export function minutesOf(t?: string): number | null {
  if (!t) return null
  const m = t.match(/^(\d{1,2}):(\d{2})/)
  const hh = m?.[1]
  const mm = m?.[2]
  if (hh == null || mm == null) return null
  return parseInt(hh, 10) * 60 + parseInt(mm, 10)
}

function sameSubject(a: BrockenLesson, b: BrockenLesson): boolean {
  if (a.subjectId && b.subjectId) return a.subjectId === b.subjectId
  return (a.subjectName || '') === (b.subjectName || '')
}

function sameKlass(a: BrockenLesson, b: BrockenLesson): boolean {
  return (a.klassName || '') === (b.klassName || '')
}

function adjacent(a: BrockenLesson, b: BrockenLesson): boolean {
  if (a.date !== b.date) return false
  if (!sameSubject(a, b) || !sameKlass(a, b)) return false
  const aStart = minutesOf(a.startTime)
  const aEnd = minutesOf(a.endTime)
  const bStart = minutesOf(b.startTime)
  const bEnd = minutesOf(b.endTime)
  if (aStart == null || aEnd == null || bStart == null || bEnd == null) {
    return false
  }
  const firstEnd = aStart <= bStart ? aEnd : bEnd
  const secondStart = aStart <= bStart ? bStart : aStart
  if (secondStart < firstEnd) return false
  return secondStart - firstEnd <= GAP_MAX_MINUTES
}

function fingerprint(slotIds: string[]): string {
  return [...slotIds].sort().join(',')
}

function hhmm(t?: string): string {
  return (t || '').slice(0, 5)
}

function unitLabel(date: string, slots: string[], lessons: BrockenLesson[]): string {
  const wd = WD[weekdayOf(date)] || ''
  const day = date.slice(8, 10) + '.' + date.slice(5, 7) + '.'
  const names = lessons.map(l => l.subjectName).filter(Boolean)
  const subj = names[0] ? ` ${names[0]}` : ''
  if (slots.length >= 2) return `${wd} ${day} Doppel${subj}`.trim()
  return `${wd} ${day}${subj}`.trim()
}

function shortLabel(lessons: BrockenLesson[]): string {
  const t = hhmm(lessons[0]?.startTime)
  const subj = lessons[0]?.subjectName || ''
  const bit = [t, subj].filter(Boolean).join(' ')
  return bit || 'Stück'
}

function makeUnit(date: string, group: BrockenLesson[]): BrockenUnit {
  const lessons = [...group].sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''))
  const slots = [...new Set(lessons.map(l => l.timeSlotId))]
  return {
    date,
    timeSlotIds: slots,
    label: unitLabel(date, slots, lessons),
    shortLabel: shortLabel(lessons),
    weekday: weekdayOf(date),
    fingerprint: fingerprint(slots),
    lessons,
    cutAfter: null,
  }
}

function gapMinutes(a: BrockenLesson, b: BrockenLesson): number {
  const aEnd = minutesOf(a.endTime)
  const bStart = minutesOf(b.startTime)
  if (aEnd == null || bStart == null) return 0
  return Math.max(0, bStart - aEnd)
}

function cutBetween(prev: BrockenLesson, next: BrockenLesson): BrockenCut {
  const minutes = gapMinutes(prev, next)
  if (!sameSubject(prev, next)) return { kind: 'subject', minutes }
  if (!sameKlass(prev, next)) return { kind: 'class', minutes }
  return { kind: 'pause', minutes }
}

function finalizeDay(dayUnits: BrockenUnit[]): BrockenUnit[] {
  const sorted = [...dayUnits].sort((a, b) =>
    (a.lessons[0]?.startTime || '').localeCompare(b.lessons[0]?.startTime || ''),
  )
  return sorted.map((u, i) => {
    const next = sorted[i + 1]
    if (!next) return { ...u, cutAfter: null }
    const prevLast = u.lessons[u.lessons.length - 1]
    const nextFirst = next.lessons[0]
    if (!prevLast || !nextFirst) return { ...u, cutAfter: null }
    return { ...u, cutAfter: cutBetween(prevLast, nextFirst) }
  })
}

export function buildBrockenUnits(
  lessons: BrockenLesson[],
  opts?: { keepTogether?: boolean },
): BrockenUnit[] {
  const keepTogether = opts?.keepTogether !== false
  const byDate = new Map<string, BrockenLesson[]>()
  for (const l of lessons) {
    if (!l.date || !l.timeSlotId) continue
    const list = byDate.get(l.date) || []
    list.push(l)
    byDate.set(l.date, list)
  }
  const units: BrockenUnit[] = []
  for (const [date, dayLessons] of [...byDate.entries()].sort()) {
    const sorted = [...dayLessons].sort((a, b) =>
      (a.startTime || '').localeCompare(b.startTime || ''),
    )
    const dayUnits: BrockenUnit[] = []
    if (!keepTogether) {
      for (const l of sorted) dayUnits.push(makeUnit(date, [l]))
      units.push(...finalizeDay(dayUnits))
      continue
    }
    const parent = sorted.map((_, i) => i)
    const find = (i: number): number => {
      const p = parent[i]
      if (p == null || p === i) return i
      const root = find(p)
      parent[i] = root
      return root
    }
    for (let i = 0; i < sorted.length; i++) {
      for (let j = i + 1; j < sorted.length; j++) {
        const a = sorted[i]
        const b = sorted[j]
        if (a && b && adjacent(a, b)) {
          const ri = find(i)
          const rj = find(j)
          if (ri !== rj) parent[ri] = rj
        }
      }
    }
    const groups = new Map<number, BrockenLesson[]>()
    for (let i = 0; i < sorted.length; i++) {
      const lesson = sorted[i]
      if (!lesson) continue
      const r = find(i)
      const g = groups.get(r) || []
      g.push(lesson)
      groups.set(r, g)
    }
    for (const group of groups.values()) dayUnits.push(makeUnit(date, group))
    units.push(...finalizeDay(dayUnits))
  }
  return units
}

export function brockenSeriesKey(unit: BrockenUnit, mode: WeekRankMode = 'repeat'): string {
  if (mode === 'each') return `${isoMonday(unit.date)}:${unit.weekday}:${unit.fingerprint}`
  return `${unit.weekday}:${unit.fingerprint}`
}

export function cutLabel(cut: BrockenCut): string {
  if (cut.kind === 'subject') return 'Anderes Fach'
  if (cut.kind === 'class') return 'Andere Klasse'
  return cut.minutes > 0 ? `Pause ${cut.minutes} Min` : 'Pause'
}
